import { useEffect, useState } from 'react';

/**
 * Progress messaging for slow AI calls (master spec Phase 7 item 2 / R10:
 * "no spinner longer than 3s without progress messaging").
 *
 * Returns the label to show next to a spinner. For the first DELAY_MS it
 * returns the base label, because a fast response that flickers through three
 * status messages reads as broken. After that it steps through `steps` so a
 * student or teacher waiting on Gemini can see the wait is progressing.
 *
 * The steps are descriptions of work the request is genuinely doing. Do not add
 * theatre here: an invented step ("consulting the rubric" when no rubric was
 * sent) is a small lie on a screen a student is staring at.
 */
const DELAY_MS = 3000;
const STEP_MS = 2600;

export function useRotatingStatus(active, base, steps = []) {
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    if (!active || steps.length === 0) {
      setIndex(-1);
      return undefined;
    }
    const start = setTimeout(() => setIndex(0), DELAY_MS);
    const step = setInterval(
      () => setIndex((i) => (i < 0 ? 0 : Math.min(i + 1, steps.length - 1))),
      DELAY_MS + STEP_MS,
    );
    return () => {
      clearTimeout(start);
      clearInterval(step);
    };
    // steps is a literal array at every call site; length is the stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, steps.length]);

  if (!active) return base;
  return index < 0 ? base : steps[index];
}

export default useRotatingStatus;
